import React from "react";
import { Page, Text, BlockStack, Badge, Box } from "@shopify/polaris";
import { AlertTriangleIcon, CheckCircleIcon, LockIcon, StarFilledIcon } from "@shopify/polaris-icons";
import { Icon } from "@shopify/polaris";
import { APP_DISPLAY_NAME } from "../config/appBranding";
import { useAppTitleBar } from "../hooks/useAppTitleBar";

const PLAN_FEATURES = [
  { label: "Storefront age gate popup" },
  { label: "Custom popup design & branding" },
  { label: "Display rules by page & collection" },
  { label: "Country & language templates" },
  { label: "Verification analytics dashboard" },
  { label: "Theme app embed — no code" },
  { label: "Birthdate, checkbox & yes/no", tag: "Popular", tagTone: "warning" },
  { label: "Checkout verification (Plus)", tag: "Plus", tagTone: "attention" },
  { label: "Cookie-based return visitor memory" },
];

function PlanFeature({ label, tag, tagTone }) {
  return (
    <div className="age-plans-feature">
      <span className="age-plans-feature__icon" aria-hidden>
        <Icon source={CheckCircleIcon} tone="success" />
      </span>
      <span className="age-plans-feature__label">
        <Text as="span" variant="bodyMd">
          {label}
        </Text>
        {tag ? (
          <Badge tone={tagTone || "info"} size="small">
            {tag}
          </Badge>
        ) : null}
      </span>
    </div>
  );
}

export default function PlansPage() {
  useAppTitleBar({ title: "Plans & Billing" });

  return (
    <Page>
      <BlockStack gap="500">
        <div className="age-plans-hero">
          <div className="age-plans-hero__icon" aria-hidden>
            <Icon source={LockIcon} tone="success" />
          </div>
          <div className="age-plans-hero__body">
            <span className="age-plans-hero__badge">
              <Icon source={StarFilledIcon} />
              EARLY ACCESS — FREE RIGHT NOW
            </span>
            <Text as="h2" variant="headingMd">
              Full access at no cost while we finish launching {APP_DISPLAY_NAME}.
            </Text>
            <Text as="p" variant="bodyMd">
              Your store currently has every feature unlocked at <strong>$0 / month</strong>. Paid billing is{" "}
              <strong>not active yet</strong> — we will start charging soon.
            </Text>
          </div>
        </div>

        <div className="age-plans-card">
          <Box padding="500">
            <BlockStack gap="500">
              <div className="age-plans-card__header">
                <Text as="h2" variant="headingLg">
                  Early Access Plan
                </Text>
                <div className="age-plans-card__badges">
                  <Badge tone="success">Active</Badge>
                  <span className="age-plans-badge-grandfathered">
                    <Icon source={StarFilledIcon} />
                    Free during launch
                  </span>
                </div>
              </div>

              <div className="age-plans-pricing">
                <div className="age-plans-pricing__row">
                  <p className="age-plans-pricing__main">
                    <span className="age-plans-pricing__amount">$0</span>
                    <span className="age-plans-pricing__period"> / month</span>
                  </p>
                  <span className="age-plans-pricing__savings">
                    <Icon source={StarFilledIcon} />
                    No charges while early access is live
                  </span>
                </div>
                <Text as="p" variant="bodyMd" tone="subdued">
                  All age verification popups, display rules, analytics, and customization tools are included at no cost
                  today. Paid billing will be introduced soon.
                </Text>
              </div>

              <BlockStack gap="300">
                <Text as="h3" variant="headingSm" fontWeight="semibold">
                  EVERYTHING INCLUDED
                </Text>
                <div className="age-plans-features-grid">
                  {PLAN_FEATURES.map((feature) => (
                    <PlanFeature key={feature.label} {...feature} />
                  ))}
                </div>
              </BlockStack>
            </BlockStack>
          </Box>
        </div>

        <div className="age-plans-footnotes">
          <div className="age-plans-footnote age-plans-footnote--success">
            <span className="age-plans-footnote__icon" aria-hidden>
              <Icon source={CheckCircleIcon} tone="success" />
            </span>
            <div>
              <Text as="p" variant="bodyMd" fontWeight="semibold">
                No billing today — $0 / month
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                There is nothing to approve or subscribe to right now. Keep using {APP_DISPLAY_NAME} as usual.
              </Text>
            </div>
          </div>

          <div className="age-plans-footnote age-plans-footnote--warning">
            <span className="age-plans-footnote__icon" aria-hidden>
              <Icon source={AlertTriangleIcon} tone="warning" />
            </span>
            <div>
              <Text as="p" variant="bodyMd" fontWeight="semibold">
                Paid billing is coming soon
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                We are preparing to introduce a paid plan. Billing is not available in the app yet — all features remain
                free during early access.
              </Text>
            </div>
          </div>
        </div>
      </BlockStack>
    </Page>
  );
}
